import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { optionLabel } from "../../helpers/optionLabels";
import { Box, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  QuoteApprovalStatus,
  useCreateServiceChargeQuoteMutation,
  useGetQuotesByAccountQuery,
  useRespondToQuoteMutation,
} from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { useS3 } from "hooks/useS3";
import { headerOffset } from "components/basic/header";
import ScrollContainer from "components/basic/scrollContainer";
import Typography from "components/basic/typography";
import logger from "utils/logger";
import { track } from "analytics";
import QuoteActionCard from "./quoteActionCard";
import BookVisitDrawer from "./bookVisitDrawer";
import { useQuoteBooking } from "./bookVisitDrawer/bookVisitApi";
import { useMessagePopup } from "pages/dashboard/pages/message/components/ChatPopups";
import QuoteHeader from "./quoteHeader";
import QuoteParties from "./quoteParties";
import QuoteLineItems from "./quoteLineItems";
import QuoteCustomization from "./quoteCustomization";
import QuoteSection from "./quoteSection";
import QuoteInfoList from "./quoteInfoList";
import QuoteDetailList, { QuoteDetailItem } from "./quoteDetailList";
import QuoteFlowNav, { QUOTE_FLOW_NAV_HEIGHT } from "./quoteFlowNav";
import { haversineKm } from "utils/geo";
import {
  QUOTE_REGISTRATION_LABEL,
  QuoteInstallationDetails,
  QuoteNavigationState,
  QuotePageData,
  buildMockQuotePageData,
  buildQuotePageDataFromService,
  addOnSignature,
  buildQuoteSubmissionInputs,
  computeQuoteTotals,
  isQuoteExpired,
} from "./quoteTools";
import { clearEstimatorDrafts } from "../../helpers/estimatorDraft";

/** Map submitted installation details into read-only label/value rows. */
const toInstallationDetailItems = (
  details: QuoteInstallationDetails,
  t: TFunction<"estimator">,
): QuoteDetailItem[] => {
  const answer = (value?: string | null) => optionLabel(t, value);
  const yesNo = (value: boolean) => answer(value ? "Yes" : "No");
  return [
    { label: t("quote.installation_address"), value: details.address },
    {
      label: t("quote.installation_type"),
      value: answer(details.installationType),
    },
    { label: t("quote.vehicle"), value: answer(details.vehicleType) },
    { label: t("quote.house_type"), value: answer(details.houseType) },
    {
      label: t("quote.charger_location"),
      value: answer(details.chargerLocation),
    },
    { label: t("quote.charger_setup"), value: answer(details.chargerSetup) },
    {
      label: t("quote.panel_location"),
      value: answer(details.electricalPanelLocation),
    },
    { label: t("quote.service_size"), value: answer(details.mainServiceSize) },
    { label: t("quote.wire_length"), value: answer(details.wireLength) },
    { label: t("quote.purchase_type"), value: answer(details.purchaseType) },
    { label: t("quote.elevator"), value: yesNo(details.elevator) },
    { label: t("quote.pool"), value: yesNo(details.pool) },
    { label: t("quote.hot_tub"), value: yesNo(details.hotTub) },
    {
      label: t("quote.additional"),
      value: details.additionalInformation ?? "",
    },
    {
      label: t("quote.panel_photo"),
      value: details.electricalPanelPhotoURL ? t("quote.view_photo") : "",
      href: details.electricalPanelPhotoURL,
    },
    {
      label: t("quote.breaker_photo"),
      value: details.mainBreakerPhotoURL ? t("quote.view_photo") : "",
      href: details.mainBreakerPhotoURL,
    },
  ];
};

interface QuoteProps {
  /**
   * Quote data passed from the previous page (ChargerCard). Build it with
   * buildQuotePageDataFromService() from quoteTools. Defaults to mock data so
   * the screen renders standalone during development.
   */
  quoteData?: QuotePageData;
  /**
   * Render the quote as a read-only detail view (e.g. a saved quote opened from
   * the dashboard): customization is locked and the vendor action card +
   * booking flow are hidden.
   */
  readOnly?: boolean;
  /** Scroll container height. Defaults to the full estimator viewport height. */
  height?: string;
}

const Quote: React.FC<QuoteProps> = ({
  quoteData,
  readOnly = false,
  height,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const location = useLocation();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { uploadToS3 } = useS3();
  const [{ fetching: isBooking }, createServiceChargeQuote] =
    useCreateServiceChargeQuoteMutation();
  const [{ fetching: responding }, respondToQuote] =
    useRespondToQuoteMutation();
  const pageHeight = height ?? `calc(100dvh - ${headerOffset(1)})`;

  // Data passed from ChargerCard via React Router location state (if any).
  const navigationState = location.state as QuoteNavigationState | null;

  // Resolve the data source in priority order:
  //  1. `quoteData` — an explicit prop passed directly to <Quote /> (e.g. a
  //     parent/test that already built the data). Highest priority.
  //  2. ChargerCard's selection — the QuoteNavigationState placed on
  //     location.state by navigate(..., { state }) when the user expands a
  //     ChargerCard. Rebuilt into QuotePageData via buildQuotePageDataFromService.
  //  3. Mock data — standalone/dev fallback so the page renders on its own.
  const data = useMemo(() => {
    if (quoteData) {
      return quoteData;
    }
    if (navigationState?.service) {
      logger.log("Building quote data from navigation state:", navigationState);
      return buildQuotePageDataFromService(
        navigationState.service,
        navigationState.chargerConfigData,
        navigationState.selected,
      );
    }
    logger.log("No quote data prop or navigation state found, using mock data");
    return buildMockQuotePageData();
  }, [quoteData, navigationState]);

  // Customization is editable on this screen and recalculates totals live.
  const [selectedChargerModelId, setSelectedChargerModelId] = useState(
    data.customization.selectedChargerModelId,
  );
  const [selectedAddOnId, setSelectedAddOnId] = useState(
    data.customization.selectedAddOnId,
  );

  // Paging to another vendor's quote swaps `data` while this screen stays
  // mounted, so the selections have to follow the new quote's defaults.
  useEffect(() => {
    setSelectedChargerModelId(data.customization.selectedChargerModelId);
    setSelectedAddOnId(data.customization.selectedAddOnId);
  }, [data]);

  // Estimator-flow navigation. Only shown when the user arrived from the
  // select-estimate page (the dashboard opens this screen read-only, with no
  // flow to page through).
  const flowServices = useMemo(
    () => navigationState?.services ?? [],
    [navigationState],
  );
  const currentServiceIndex = navigationState?.serviceIndex ?? 0;
  // Paging wraps around, so the last quote leads back to the first one.
  const nextServiceIndex =
    flowServices.length > 0
      ? (currentServiceIndex + 1) % flowServices.length
      : 0;
  const nextService =
    flowServices.length > 1 ? (flowServices[nextServiceIndex] ?? null) : null;
  const showFlowNav = !readOnly && !quoteData;

  const handleBackToEstimates = useCallback(() => {
    navigate("?currentPage=selectEstimate");
  }, [navigate]);

  // Open the next recommendation with its default selections (the builder
  // derives them from the service when none are passed).
  const handleNextQuote = useCallback(() => {
    if (!navigationState || !nextService) {
      return;
    }

    const nextNavigationState: QuoteNavigationState = {
      service: nextService,
      chargerConfigData: navigationState.chargerConfigData,
      services: flowServices,
      serviceIndex: nextServiceIndex,
    };

    navigate("?currentPage=quote", { state: nextNavigationState });
  }, [navigate, navigationState, nextService, flowServices, nextServiceIndex]);

  const totals = useMemo(
    () => computeQuoteTotals(data, selectedChargerModelId, selectedAddOnId),
    [data, selectedChargerModelId, selectedAddOnId],
  );

  const liveDistanceText = useMemo(() => {
    const distanceKm = haversineKm(
      navigationState?.chargerConfigData?.latitude,
      navigationState?.chargerConfigData?.longitude,
      navigationState?.service?.vendor?.latitude,
      navigationState?.service?.vendor?.longitude,
    );

    if (distanceKm == null) {
      return data.vendor.distanceText;
    }

    return `${distanceKm.toFixed(1)} km away`;
  }, [
    navigationState?.chargerConfigData?.latitude,
    navigationState?.chargerConfigData?.longitude,
    navigationState?.service?.vendor?.latitude,
    navigationState?.service?.vendor?.longitude,
    data.vendor.distanceText,
  ]);

  /**
   * Ensure estimator photos are uploaded before creating the service request.
   * We only upload when a File exists and its corresponding URL is still empty.
   */
  const ensurePhotoUrls = useCallback(
    async (config: QuoteNavigationState["chargerConfigData"]) => {
      const resolvedConfig = { ...config };

      if (
        !resolvedConfig.electricalPanelPhotoURL &&
        resolvedConfig.electricalPanelPhoto
      ) {
        const electricalPanelPhotoURL = await uploadToS3(
          resolvedConfig.electricalPanelPhoto,
          "ev-charger-service/electrical-panel-photos",
        );
        if (!electricalPanelPhotoURL) {
          showSnack(t("quote.panel_upload_failed"), "error");
          return null;
        }
        resolvedConfig.electricalPanelPhotoURL = electricalPanelPhotoURL;
      }

      if (
        !resolvedConfig.mainBreakerPhotoURL &&
        resolvedConfig.mainBreakerPhoto
      ) {
        const mainBreakerPhotoURL = await uploadToS3(
          resolvedConfig.mainBreakerPhoto,
          "ev-charger-service/main-breaker-photos",
        );
        if (!mainBreakerPhotoURL) {
          showSnack(t("quote.breaker_upload_failed"), "error");
          return null;
        }
        resolvedConfig.mainBreakerPhotoURL = mainBreakerPhotoURL;
      }

      return resolvedConfig;
    },
    [showSnack, uploadToS3, t],
  );

  // Which action card button is waiting on the create-quote mutation, and the
  // quote the "Book a Call" scheduling drawer is open for (null = closed).
  const [pendingAction, setPendingAction] = useState<
    "approve" | "save" | "bookVisit" | "message" | null
  >(null);
  const [bookVisitQuoteId, setBookVisitQuoteId] = useState<number | null>(null);
  // The quote created from this screen during this visit (covers the window
  // before the saved-quotes list refetches). Reset when the customization
  // changes — a modified estimate is a new quote.
  const [createdQuoteId, setCreatedQuoteId] = useState<number | null>(null);
  const messagePopup = useMessagePopup();
  const [{ data: myQuotesData }, refetchMyQuotes] = useGetQuotesByAccountQuery({
    pause: readOnly,
  });

  useEffect(() => {
    setCreatedQuoteId(null);
  }, [selectedChargerModelId, selectedAddOnId]);

  // A still-valid quote for this exact estimate (same service, charger model,
  // and add-on) already on the account — survives across devices and cleared
  // caches. Expired ones are skipped so the estimate can be requested again.
  const existingQuoteId = useMemo(() => {
    const chargerLabel = data.customization.chargerModelOptions.find(
      (o) => o.id === selectedChargerModelId,
    )?.label;
    const addOn = data.customization.addOnOptions.find(
      (o) => o.id === selectedAddOnId,
    );
    // The estimator offers one add-on, but a quote stores a list, so compare
    // through the same canonical signature the server dedupes on.
    const selectedSignature = addOnSignature(
      addOn && addOn.id !== 0 && addOn.price > 0
        ? [{ label: addOn.label, price: addOn.price }]
        : [],
    );

    const matches = (myQuotesData?.getQuotesByAccount ?? []).filter(
      (q) =>
        !isQuoteExpired(q) &&
        q.evChargerService.id === data.serviceId &&
        q.chargerModel === chargerLabel &&
        addOnSignature(q.addOns) === selectedSignature,
    );
    // Most recent match wins if several exist (e.g. pre-dedupe duplicates).
    return matches.length
      ? matches.reduce((a, b) =>
          (a.updatedAt ?? "") >= (b.updatedAt ?? "") ? a : b,
        ).id
      : null;
  }, [myQuotesData, data, selectedChargerModelId, selectedAddOnId]);

  // The estimate already sent to this vendor, from either source.
  const knownQuoteId = createdQuoteId ?? existingQuoteId;

  // The saved row behind it, when the list has caught up — a quote approved on
  // an earlier visit must not be answered a second time.
  const knownQuote = useMemo(
    () =>
      (myQuotesData?.getQuotesByAccount ?? []).find(
        (q) => q.id === knownQuoteId,
      ) ?? null,
    [myQuotesData, knownQuoteId],
  );

  // An appointment already scheduled against that estimate flips "Book a Call"
  // into a reschedule action. Customers can only request a video or phone
  // call; an "onsite" booking here was arranged by the vendor.
  const { booking: activeBooking } = useQuoteBooking(knownQuoteId);
  const bookVisitLabel = activeBooking
    ? activeBooking.type === "onsite"
      ? t("quote.reschedule_visit")
      : t("quote.reschedule_call")
    : t("quote.book_call");

  // Persist the service request + quote to the backend; returns the created
  // quote's id (null on failure — errors are shown via snacks).
  const persistQuote = useCallback(async (): Promise<number | null> => {
    const chargerConfigData = navigationState?.chargerConfigData;
    logger.log("Current charger config data:", chargerConfigData);
    if (!chargerConfigData) {
      showSnack(t("quote.missing_details"), "error");
      return null;
    }

    const resolvedChargerConfigData = await ensurePhotoUrls(chargerConfigData);
    if (!resolvedChargerConfigData) {
      return null;
    }

    const { serviceRequestInput, quoteInput } = buildQuoteSubmissionInputs(
      data,
      resolvedChargerConfigData,
      selectedChargerModelId,
      selectedAddOnId,
      liveDistanceText,
    );

    logger.log(
      "serviceRequestInput:",
      serviceRequestInput,
      "quoteInput:",
      quoteInput,
    );

    try {
      const response = await createServiceChargeQuote({
        serviceRequestInput,
        quoteInput,
      });

      logger.log("Response from createServiceChargeQuote:", response);

      const result = response.data?.createServiceChargeQuote;
      if (result?.errors?.length) {
        result.errors.forEach((err) =>
          showSnack(
            `${err.field ?? "error"}: ${err.message ?? t("quote.invalid_input")}`,
            "error",
          ),
        );
        return null;
      }
      const quoteId = result?.evChargerServiceRequest?.quotes?.[0]?.id ?? null;
      if (quoteId != null) {
        setCreatedQuoteId(quoteId);
        clearEstimatorDrafts();
        // The end of the estimator funnel: everything before this is a step a
        // visitor can abandon.
        track("estimator_submitted", { quoteId });
        // Pull the new quote into the saved-quotes list so the server-derived
        // dedupe check stays in sync.
        refetchMyQuotes({ requestPolicy: "network-only" });
      }
      return quoteId;
    } catch (error) {
      logger.error("Creating the quote failed:", error);
      showSnack(t("quote.send_failed"), "error");
      return null;
    }
  }, [
    t,
    navigationState,
    data,
    selectedChargerModelId,
    selectedAddOnId,
    ensurePhotoUrls,
    createServiceChargeQuote,
    showSnack,
    liveDistanceText,
    refetchMyQuotes,
  ]);

  // The estimate already on the account (any device) or created during this
  // visit — otherwise a freshly persisted one.
  const ensureQuote = useCallback(
    async (): Promise<number | null> => knownQuoteId ?? (await persistQuote()),
    [knownQuoteId, persistQuote],
  );

  // "Save for Later" files the estimate with the vendor and leaves it pending,
  // so the user can approve it from the dashboard whenever they are ready.
  const handleSaveForLater = useCallback(async () => {
    logger.log("Save for Later clicked");
    if (pendingAction) {
      return;
    }
    setPendingAction("save");
    try {
      const alreadySent = knownQuoteId != null;
      const quoteId = await ensureQuote();
      if (quoteId != null) {
        showSnack(
          alreadySent ? t("quote.already_saved") : t("quote.saved"),
          "success",
        );
      }
    } finally {
      setPendingAction(null);
    }
  }, [pendingAction, knownQuoteId, ensureQuote, showSnack, t]);

  // Approving both files the estimate and answers it, so the vendor is notified
  // without the user having to approve again from the dashboard.
  const handleApproveEstimate = useCallback(async () => {
    logger.log("Approve Estimate clicked");
    if (pendingAction) {
      return;
    }
    setPendingAction("approve");
    try {
      const quoteId = await ensureQuote();
      if (quoteId == null) {
        return;
      }

      if (knownQuote?.approvalStatus === QuoteApprovalStatus.Approved) {
        showSnack(t("quote.already_approved"), "success");
        return;
      }

      const response = await respondToQuote({
        id: quoteId,
        response: "approve",
      });
      const payload = response.data?.respondToQuote;

      if (!payload?.quote) {
        // The estimate is filed either way — say so, and point at where it can
        // still be approved (an expired quote is refused here, for instance).
        showSnack(
          payload?.errors?.[0]?.message ?? t("quote.approve_failed"),
          "error",
        );
        return;
      }

      showSnack(t("quote.approved"), "success");
      refetchMyQuotes({ requestPolicy: "network-only" });
    } finally {
      setPendingAction(null);
    }
  }, [
    t,
    pendingAction,
    knownQuote,
    ensureQuote,
    respondToQuote,
    showSnack,
    refetchMyQuotes,
  ]);

  // "Book a Call" first persists the quote (same as saving for later, unless
  // one was already created), then opens the scheduling drawer against it.
  const handleBookSiteVisit = useCallback(async () => {
    if (pendingAction) {
      return;
    }
    setPendingAction("bookVisit");
    try {
      const quoteId = await ensureQuote();
      if (quoteId != null) {
        showSnack(t("quote.quote_saved"));
        setBookVisitQuoteId(quoteId);
      }
    } finally {
      setPendingAction(null);
    }
  }, [pendingAction, ensureQuote, showSnack, t]);

  // "Message Vendor" persists the quote (unless one was already created), then
  // opens the floating chat with the vendor.
  const handleMessageVendor = useCallback(async () => {
    logger.log("Message Vendor clicked");
    if (pendingAction) {
      return;
    }
    setPendingAction("message");
    try {
      const quoteId = await ensureQuote();
      if (quoteId != null) {
        await messagePopup(data.vendor.id);
      }
    } finally {
      setPendingAction(null);
    }
  }, [pendingAction, ensureQuote, messagePopup, data.vendor.id]);

  // The document and the action card need ~1300px side by side, so below lg
  // they stack and each take the full width.
  const pageSx = {
    display: "flex",
    flexDirection: { xs: "column", lg: "row" },
    alignItems: { xs: "stretch", lg: "flex-start" },
    gap: theme.spacing(4),
    padding: {
      xs: theme.spacing(2),
      sm: theme.spacing(3),
      lg: theme.spacing(4),
    },
  };

  const documentSx = {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    maxWidth: "760px",
  };

  const termsItems = [
    data.meta.validityText,
    ...(data.paymentTerms ? [data.paymentTerms] : []),
  ];

  // The bottom nav takes its height out of the scrollable area.
  const contentHeight = showFlowNav
    ? `calc(${pageHeight} - ${QUOTE_FLOW_NAV_HEIGHT}px)`
    : pageHeight;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: pageHeight }}
    >
      <ScrollContainer height={contentHeight}>
        <Box sx={pageSx}>
          <Box sx={documentSx}>
            <QuoteHeader
              logoUrl={data.vendor.logoUrl}
              businessName={data.vendor.businessName}
              address={data.vendor.address}
              email={data.vendor.email}
              phoneNumber={data.vendor.phoneNumber}
              website={data.vendor.website}
            />

            <QuoteParties
              customerName={data.customer.fullName}
              customerAddress={data.customer.address}
              customerPhone={data.customer.phoneNumber}
              customerEmail={data.customer.email}
              estimateNumber={data.meta.estimateNumber}
              date={data.meta.date}
            />

            <QuoteLineItems
              items={totals.lineItems}
              subtotal={totals.subtotal}
              tax={totals.tax}
              total={totals.total}
              taxLabel={data.meta.taxLabel}
            />

            <QuoteSection title={t("quote.inclusions")}>
              <QuoteInfoList items={data.inclusions} variant="plain" />
            </QuoteSection>

            <QuoteCustomization
              chargerModelOptions={data.customization.chargerModelOptions}
              addOnOptions={data.customization.addOnOptions}
              selectedChargerModelId={selectedChargerModelId}
              selectedAddOnId={selectedAddOnId}
              isChargerModelLocked={data.customization.isChargerModelLocked}
              readOnly={readOnly}
              onChargerModelChange={setSelectedChargerModelId}
              onAddOnChange={setSelectedAddOnId}
            />

            {data.installationDetails ? (
              <QuoteSection title={t("quote.installation_details")}>
                <QuoteDetailList
                  items={toInstallationDetailItems(data.installationDetails, t)}
                />
              </QuoteSection>
            ) : null}

            <QuoteSection title={t("quote.assumptions")}>
              <QuoteInfoList items={data.assumptions} variant="bulleted" />
            </QuoteSection>

            {termsItems.length > 0 ? (
              <QuoteSection title={t("quote.terms")}>
                <QuoteInfoList items={termsItems} variant="bulleted" />
              </QuoteSection>
            ) : null}

            <Typography variant="body2">
              {t("quote.hst_number", { number: data.meta.hstNumber })}
            </Typography>

            {data.notes.map((note, index) => (
              <Typography
                key={index}
                variant="body2"
                style={{ fontStyle: "italic" }}
              >
                {note}
              </Typography>
            ))}
          </Box>

          {readOnly ? null : (
            <QuoteActionCard
              companyName={data.vendor.businessName}
              googleListing={data.vendor.googleListing}
              isVerified={data.vendor.isVerified}
              distanceText={liveDistanceText}
              responseText={data.vendor.responseText}
              registrationLabel={QUOTE_REGISTRATION_LABEL}
              registrationValue={data.vendor.registrationValue}
              insuranceLabel={t("quote.insurance")}
              insuranceValue={data.vendor.insuranceValue}
              description={data.vendor.about ?? ""}
              siteVisitAction={{
                label:
                  pendingAction === "approve"
                    ? t("quote.approving")
                    : t("quote.approve"),
                onClick: handleApproveEstimate,
                loading: pendingAction === "approve",
                disabled: isBooking || responding,
              }}
              bookVisitAction={{
                label: bookVisitLabel,
                onClick: handleBookSiteVisit,
                loading: pendingAction === "bookVisit",
                disabled: isBooking || responding,
              }}
              messageVendorAction={{
                label: t("quote.message_vendor"),
                onClick: handleMessageVendor,
                loading: pendingAction === "message",
                disabled: isBooking || responding,
              }}
              saveEstimateAction={{
                label:
                  pendingAction === "save"
                    ? t("quote.saving")
                    : t("quote.save_later"),
                onClick: handleSaveForLater,
                loading: pendingAction === "save",
                disabled: isBooking || responding,
              }}
            />
          )}

          {bookVisitQuoteId != null && (
            <BookVisitDrawer
              quoteId={bookVisitQuoteId}
              vendorName={data.vendor.businessName}
              quoteExpiresAt={
                (myQuotesData?.getQuotesByAccount ?? []).find(
                  (q) => q.id === bookVisitQuoteId,
                )?.expiresAt
              }
              onClose={() => setBookVisitQuoteId(null)}
            />
          )}
        </Box>
      </ScrollContainer>

      {showFlowNav && (
        <QuoteFlowNav
          onBack={handleBackToEstimates}
          onNextQuote={nextService ? handleNextQuote : undefined}
        />
      )}
    </div>
  );
};

export default Quote;
